import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import { useAuth } from '@/hooks/useAuth';
import { useDarkMode } from '@/hooks/useDarkMode';
import api from '@/services/api';
import styles from './MainLayout.module.css';

const TITLES = {
  dashboard: 'Dashboard',
  personas: 'Emprendedores',
  capacitaciones: 'Capacitaciones',
  eventos: 'Eventos',
  apoyos: 'Fortalecimiento',
  reportes: 'Reportes',
  auditoria: 'Auditoría',
  usuarios: 'Usuarios',
  configuracion: 'Configuración',
};

const SUBTITLES = {
  eventos: 'Links públicos para inscripción a capacitaciones',
  dashboard: 'Resumen general del sistema',
  personas: 'Registro único y expediente digital',
  capacitaciones: 'Gestión de formación y capacitación',
  apoyos: 'Capital semilla, ferias y asistencias técnicas',
  reportes: 'Consultas estratégicas y exportación',
  auditoria: 'Bitácora de acciones del sistema',
  usuarios: 'Administración de funcionarios',
  configuracion: 'Parámetros del sistema',
};

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [pendingCount, setPendingCount] = useState(0);
  const currentModule = window.location.pathname.split('/')[1] || 'dashboard';

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) setCollapsed(true);
      else {
        const saved = localStorage.getItem('sidebar-collapsed');
        setCollapsed(saved ? JSON.parse(saved) : false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.rol !== 'admin' && user?.rol !== 'operador') return;

    const fetchPending = async () => {
      try {
        const res = await api.get('/personas?estado=pendiente&limite=1');
        setPendingCount(res.meta?.total || 0);
      } catch (error) {
        // Silencioso
      }
    };

    const onPendingUpdated = (event) => {
      if (typeof event.detail?.count === 'number') {
        setPendingCount(event.detail.count);
      } else {
        fetchPending();
      }
    };

    fetchPending();
    window.addEventListener('sitre:pending-personas-updated', onPendingUpdated);
    const interval = setInterval(fetchPending, 60000);
    return () => {
      window.removeEventListener('sitre:pending-personas-updated', onPendingUpdated);
      clearInterval(interval);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={styles.layout}>
      <Sidebar user={user} collapsed={collapsed} onLogout={handleLogout} />
      <div className={styles.main}>
        <Topbar
          title={TITLES[currentModule] || 'SITRE'}
          subtitle={SUBTITLES[currentModule] || 'Alcaldía de Soledad'}
          user={user}
          collapsed={collapsed}
          onMenuToggle={() => setCollapsed((prev) => !prev)}
          onNotificationClick={() => navigate('/personas?estado=pendiente')}
          onLogout={handleLogout}
          pendingCount={pendingCount}
          isDarkMode={isDarkMode}
          onToggleDarkMode={toggleDarkMode}
        />
        <div className={styles.content}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
